import throttle from 'lodash/throttle';
import { stringify } from 'query-string';

import autosuggestItems from './autosuggest-item';

import type {
  AutosuggestGateway as AutosuggestGatewayType,
  AutosuggestListener,
} from './types';
import type { Culture } from '@shared/types/context';
import type { TrackExperimentChokepoint } from '@shared/types/frontend/mini-event-tracker';

import { AUTOSUGGEST_SEARCH } from '@shared/constants/newrelic-events/event-names';
import logger from '@shared/utils-frontend/logger';

const AutosuggestGateway = ({
  autosuggestExpVal,
  culture,
  pageType,
  platform,
  trackExperimentChokepoint,
  utid,
}: {
  culture: Culture;
  platform: string;
  pageType: string;
  utid: string;
  autosuggestExpVal: string;
  trackExperimentChokepoint: TrackExperimentChokepoint;
}): AutosuggestGatewayType => {
  let calls: string[] = [];
  let listener: AutosuggestListener | null;

  const removeBeforeIndex = (array: string[], position: number) =>
    array.filter((_, idx) => idx > position);

  const fetchResults = ({ value }: { value: string }) => {
    if (autosuggestExpVal) {
      trackExperimentChokepoint('AutosuggestExp_Hotel');
    }
    const qs = stringify({
      rf: 'map',
      vrows: 10,
      autosuggestExp: autosuggestExpVal === '' ? undefined : autosuggestExpVal,
    });
    const { locale, market } = culture;
    const encodedQuery = value ? encodeURIComponent(value) : '';
    const baseUrl = '/g/autosuggest-search/api/v1/search-hotel';

    return fetch(`${baseUrl}/${market}/${locale}/${encodedQuery}?${qs}`, {
      headers: {
        Accept: 'application/json',
        'Skyscanner-utid': utid,
        pageType,
        platform,
      },
    });
  };

  return {
    input: throttle(async ({ value }) => {
      if (!listener) {
        return;
      }

      calls.push(value);
      let status;
      let duration;

      try {
        const requestTime = Date.now();
        const response = await fetchResults({ value });
        duration = Date.now() - requestTime;
        status = response.status;

        const json = await response.json();
        const items = autosuggestItems(json, value);

        const callPosition = calls.indexOf(value);
        if (callPosition !== -1) {
          calls = removeBeforeIndex(calls, callPosition);
          listener?.next(items);
        }
      } catch (err) {
        status = 500;
        listener?.error(err);
      } finally {
        logger.event(AUTOSUGGEST_SEARCH, {
          duration,
          status,
        });
      }
    }, 300),

    subscribe: (l) => {
      listener = l;
    },

    unsubscribe: () => {
      listener = null;
    },
  };
};

export default AutosuggestGateway;
